import { useEffect, useMemo, useState } from 'react';

import useStatesAndCounties from '@/hooks/useStatesAndCounties';
import { CountyProps } from '@/models/County';
import { ProgramNames } from '@/models/Program';
import { ReviewerInvitationProps } from '@/models/ReviewerInvitation';
import { UserProps, UserType } from '@/models/User';
import { USCountryId } from '@/utils/constants';
import { isEmail } from '@/utils/emailUtil';

export type ReviewerInfo = {
  firstName: string;
  lastName: string;
  email: string;
};

export type AssignmentInfo = {
  email: string;
  programIds: ProgramNames[];
  countryIds: number[];
  countyIds: string[];
  countryId?: number; // domestic default
  stateId?: string | null;
};

export function useReviewerAssignmentForm(args: {
  type: UserType | undefined;
  usersAll: UserProps[] | null | undefined;
  reviewers: ReviewerInvitationProps[] | null;
}) {
  const { type, usersAll, reviewers } = args;
  const { getCountiesInState } = useStatesAndCounties();

  const [selectedReviewerEmail, setSelectedReviewerEmail] = useState('');
  const [newReviewer, setNewReviewer] = useState(false);

  const [reviewerInfo, setReviewerInfo] = useState<ReviewerInfo>({
    firstName: '',
    lastName: '',
    email: '',
  });

  const [assignmentInfo, setAssignmentInfo] = useState<AssignmentInfo>({
    email: '',
    programIds: [],
    countryIds: [],
    countyIds: [],
    countryId: USCountryId,
    stateId: null,
  });

  // these back your MultiSelect controls
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramNames[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<number[]>([]);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [countiesInState, setCountiesInState] = useState<CountyProps[]>([]);

  /* ------------------------- Derived data ------------------------- */

  const applicants = useMemo(
    () => (usersAll ?? []).filter((u) => u.userType === UserType.Applicant),
    [usersAll]
  );

  const applicantEmails = useMemo(
    () => applicants.filter((a) => a?.email != null).map((a) => a.email.toLowerCase()),
    [applicants]
  );

  const reviewerEmails = useMemo(
    () => (reviewers ?? []).map((r) => (r.email ?? '').toLowerCase()).filter(Boolean),
    [reviewers]
  );

  /* ---------------------------- Effects ---------------------------- */

  useEffect(() => {
    if (!selectedState) {
      setCountiesInState([]);
      return;
    }
    setCountiesInState(getCountiesInState(selectedState));
  }, [selectedState, getCountiesInState]);

  /* ---------------------------- Handlers --------------------------- */

  const handleReviewerSelect = (value: string) => {
    setSelectedReviewerEmail(value);

    if (value === 'new') {
      setNewReviewer(true);
      return;
    }

    setNewReviewer(false);

    const email = value.toLowerCase();
    setReviewerInfo((p) => ({ ...p, email }));
    setAssignmentInfo((a) => ({ ...a, email }));
  };

  const handleReviewerInfoChange = (patch: Partial<ReviewerInfo>) => {
    setReviewerInfo((p) => ({ ...p, ...patch }));

    if (patch.email != null) {
      setAssignmentInfo((a) => ({ ...a, email: patch.email!.toLowerCase() }));
    }
  };

  const handleProgramsChange = (p: ProgramNames[]) => {
    setSelectedPrograms(p);
    setAssignmentInfo((a) => ({ ...a, programIds: p }));
  };

  const handleCountriesChange = (c: number[]) => {
    setSelectedCountries(c);
    setSelectedState(null);
    setSelectedCounties([]);

    setAssignmentInfo((a) => ({
      ...a,
      countryIds: c,
      stateId: null,
      countyIds: [],
      // keep countryId for domestic; it won't be used for InternationalPartner
    }));
  };

  const handleStateChange = (s: string | null) => {
    setSelectedState(s);
    setSelectedCountries([]);
    setSelectedCounties([]);

    setAssignmentInfo((a) => ({
      ...a,
      stateId: s,
      countryIds: [],
      countyIds: [],
      countryId: USCountryId,
    }));
  };

  const handleCountiesChange = (c: string[]) => {
    setSelectedCounties(c);
    setAssignmentInfo((a) => ({ ...a, countyIds: c }));
  };

  /* --------------------------- Validation -------------------------- */

  const isSubmitDisabled = useMemo(() => {
    if (!type) return true;
    if (!selectedReviewerEmail) return true;

    if (newReviewer) {
      if (!reviewerInfo.firstName || !reviewerInfo.lastName || !isEmail(reviewerInfo.email)) {
        return true;
      }
    }

    if (!selectedPrograms.length) return true;

    if (type === UserType.NationalProgrammeLead) {
      return !selectedCountries.length;
    }

    // Domestic types
    if (!selectedState) return true;

    if (type === UserType.RegionalCoordinator) {
      return !selectedCounties.length;
    }

    return false;
  }, [
    type,
    selectedReviewerEmail,
    newReviewer,
    reviewerInfo,
    selectedPrograms,
    selectedCountries,
    selectedState,
    selectedCounties,
  ]);

  const reset = () => {
    setSelectedReviewerEmail('');
    setNewReviewer(false);

    setReviewerInfo({ firstName: '', lastName: '', email: '' });

    setSelectedPrograms([]);
    setSelectedCountries([]);
    setSelectedState(null);
    setSelectedCounties([]);
    setCountiesInState([]);

    setAssignmentInfo({
      email: '',
      programIds: [],
      countryIds: [],
      countyIds: [],
      countryId: USCountryId,
      stateId: null,
    });
  };

  return {
    selectedReviewerEmail,
    newReviewer,
    reviewerInfo,
    assignmentInfo,

    selectedPrograms,
    selectedCountries,
    selectedState,
    selectedCounties,
    countiesInState,

    applicantEmails,
    reviewerEmails,
    isSubmitDisabled,

    handleReviewerSelect,
    handleReviewerInfoChange,
    handleProgramsChange,
    handleCountriesChange,
    handleStateChange,
    handleCountiesChange,

    reset,
  };
}
